import { Component, computed, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { SubscriptionStateService } from '../../service/subscription-state.service';
import { daysLeft, isExtensionPending } from '../../service/billing.util';

/** Dashboard deneme kartı (spec 2026-09-24 §3.2): kalan gün çubuğu, koltuk, talep durumu, iki düğme. */
@Component({
    selector: 'app-trial-status-card',
    standalone: true,
    imports: [RouterModule, TranslateModule, ButtonModule],
    template: `
        @if (visible()) {
            <div class="card !mb-4" data-testid="trial-card">
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <div class="flex flex-col gap-1">
                        <span class="font-semibold">{{ 'COMMON.TRIAL.CARD_TITLE' | translate }}</span>
                        <span class="text-sm text-muted-color">{{ 'COMMON.TRIAL.CARD_DAYS' | translate: { days: days() } }} · {{ seatsKey() | translate: { used: seatsUsed(), seats: seats() } }}</span>
                    </div>
                    <div class="flex flex-wrap gap-2">
                        @if (canRequestExtension()) {
                            <p-button [label]="'COMMON.TRIAL.EXTEND_ACTION' | translate" [outlined]="true" size="small" routerLink="/subscription" data-testid="trial-card-extend" />
                        }
                        <p-button [label]="'COMMON.TRIAL.BUY_ACTION' | translate" size="small" routerLink="/subscription" data-testid="trial-card-buy" />
                    </div>
                </div>
                @if (progressMax(); as max) {
                    <div
                        class="h-2 rounded bg-surface-200 dark:bg-surface-700 mt-3"
                        role="progressbar"
                        aria-valuemin="0"
                        [attr.aria-valuemax]="max"
                        [attr.aria-valuenow]="days()"
                        [attr.aria-label]="'COMMON.TRIAL.CARD_DAYS' | translate: { days: days() }"
                        data-testid="trial-progress"
                    >
                        <div class="h-2 rounded bg-primary" [style.width.%]="pct()"></div>
                    </div>
                }
                @if (extensionKey(); as k) {
                    <p class="text-sm mt-3 mb-0" data-testid="trial-card-extension">{{ k | translate: extensionParams() }}</p>
                }
            </div>
        }
    `
})
export class TrialStatusCardComponent {
    private state = inject(SubscriptionStateService);
    private translate = inject(TranslateService);

    readonly visible = computed(() => this.state.loaded() && !this.state.statusFailed() && this.state.normalizedStatus() === 'Trial');
    readonly days = computed(() => {
        const s = this.state.status();

        // Düzeltme turu 1, bulgu 1: negatif gün metne sızmasın (ör. sunucu saati kaymışsa).
        return Math.max(0, s?.trial?.daysLeft ?? daysLeft(s?.periodEnd) ?? 0);
    });
    /**
     * Çubuğun üst sınırı (gün): sunucunun `trial.totalDays`ı (başlangıç→bitiş, uzatma dahil; 2026-10-14). Kalan gün
     * toplamı aşamaz (final inceleme M4: aria-valuemax). Toplam yoksa (eski API) null → çubuk gizlenir.
     */
    readonly progressMax = computed<number | null>(() => {
        const total = this.state.status()?.trial?.totalDays;

        return typeof total === 'number' && total > 0 ? Math.max(this.days(), total) : null;
    });
    /** Kalan/toplam yüzdesi; toplam yoksa 0 (çubuk zaten gizli). */
    readonly pct = computed(() => {
        const max = this.progressMax();

        return max ? Math.round((this.days() / max) * 100) : 0;
    });
    readonly seatsUsed = computed(() => {
        const u = this.state.status()?.seatUsage;

        return (u?.approved ?? 0) + (u?.pending ?? 0);
    });
    readonly seats = computed(() => this.state.status()?.seatUsage?.seats ?? 0);
    /** Düzeltme turu 1, bulgu 2: `seats` null/0 = sınırsız (billing.model.ts §SeatUsage); "X / 0" gösterme. */
    readonly seatsUnlimited = computed(() => !this.state.status()?.seatUsage?.seats);
    readonly seatsKey = computed(() => (this.seatsUnlimited() ? 'COMMON.TRIAL.CARD_SEATS_UNLIMITED' : 'COMMON.TRIAL.CARD_SEATS'));
    /** Uzatma yalnız bizim self-servis/admin denemesinde ve bir kez (spec §2.2). */
    readonly canRequestExtension = computed(() => {
        const s = this.state.status();

        return s?.source === 'Trial' && !s?.extension;
    });

    readonly extensionKey = computed<string | null>(() => {
        const e = this.state.status()?.extension;

        if (!e) return null;
        if (isExtensionPending(this.state.status())) return 'COMMON.TRIAL.EXT_STATUS_PENDING';

        return e.status === 'Rejected' ? 'COMMON.TRIAL.EXT_STATUS_REJECTED' : 'COMMON.TRIAL.EXT_STATUS_APPROVED';
    });

    readonly extensionParams = computed(() => {
        const e = this.state.status()?.extension;
        const at = e?.autoApproveAt ? new Date(e.autoApproveAt) : null;

        return {
            seats: e?.seats ?? 0,
            reason: e?.reason ?? '',
            date: at && !isNaN(at.getTime()) ? at.toLocaleDateString(this.translate.currentLang || 'tr') : ''
        };
    });
}
